import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDataChannel, useRoomContext } from '@livekit/components-react';
import { IconHandStop } from '@tabler/icons-react';
import { type Participant, RoomEvent } from 'livekit-client';

import { HAND_ATTRIBUTE, handRaisedAt } from './hand';
import { recordReaction } from './reaction-log';
import { onLocalReaction, REACTION_THROTTLE_MS, REACTION_TOPIC, REACTIONS } from './reactions';
import classes from './reactions-overlay.module.css';
import { useSoundPlayer, useSoundSettings } from './sounds';

const MAX_VISIBLE_REACTIONS = 20;
const OFFSET_STEP_PERCENT = 23;
const OFFSET_RANGE_PERCENT = 70;

type FloatingReaction = { key: number; emoji: string; name: string; offset: number };

export function ReactionsOverlay({ silent = false }: Readonly<{ silent?: boolean }>) {
    const { t } = useTranslation('reactions');
    const room = useRoomContext();
    const [reactions, setReactions] = useState<FloatingReaction[]>([]);
    const nextKeyRef = useRef(0);
    const lastReceivedAtRef = useRef(new Map<string, number>());
    const soundSettings = useSoundSettings();
    const reactionSoundsOn = !silent && soundSettings.reactions;
    const raisedHandSoundOn = soundSettings.raisedHand;
    const playSound = useSoundPlayer();

    const show = useCallback(
        (reactionId: string, participant: Participant) => {
            const reaction = REACTIONS.find(({ id }) => id === reactionId);
            if (!reaction) return;
            const key = nextKeyRef.current++;
            const name = participant.isLocal ? t('overlay.you') : (participant.name ?? participant.identity);
            const offset = (key * OFFSET_STEP_PERCENT) % OFFSET_RANGE_PERCENT;
            setReactions((current) => [
                ...current.slice(-(MAX_VISIBLE_REACTIONS - 1)),
                { key, emoji: reaction.emoji, name, offset },
            ]);
            recordReaction(room, reaction.emoji, participant);
            if (reactionSoundsOn) playSound(reaction.sound);
        },
        [playSound, reactionSoundsOn, room, t]
    );

    const remove = useCallback((key: number) => {
        setReactions((current) => current.filter((item) => item.key !== key));
    }, []);

    const onMessage = useCallback(
        ({ payload, from }: { payload: Uint8Array; from?: Participant }) => {
            if (!from) return;
            const now = Date.now();
            if (now - (lastReceivedAtRef.current.get(from.identity) ?? 0) < REACTION_THROTTLE_MS) return;
            lastReceivedAtRef.current.set(from.identity, now);
            try {
                const { id } = JSON.parse(new TextDecoder().decode(payload)) as { id?: unknown };
                if (typeof id === 'string') show(id, from);
            } catch {
                return;
            }
        },
        [show]
    );
    useDataChannel(REACTION_TOPIC, onMessage);

    useEffect(() => onLocalReaction(show), [show]);

    useEffect(() => {
        if (silent) return;
        const onAttributesChanged = (changed: Record<string, string>, participant: Participant) => {
            if (participant.isLocal || !(HAND_ATTRIBUTE in changed) || handRaisedAt(changed) <= 0) return;
            notifications.show({
                message: t('hand.raised', { name: participant.name ?? participant.identity }),
                icon: <IconHandStop size={20} />,
                color: 'warning',
                autoClose: 5000,
            });
            if (raisedHandSoundOn) playSound('raised-hand');
        };
        room.on(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
        return () => {
            room.off(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
        };
    }, [playSound, raisedHandSoundOn, room, silent, t]);

    return (
        <div aria-hidden className={classes.overlay}>
            {reactions.map(({ key, emoji, name, offset }) => (
                <div
                    key={key}
                    className={classes.reaction}
                    style={{ left: `${String(offset)}%` }}
                    onAnimationEnd={() => {
                        remove(key);
                    }}
                >
                    <span className={classes.emoji}>{emoji}</span>
                    <Text truncate className={classes.name} size="xs">
                        {name}
                    </Text>
                </div>
            ))}
        </div>
    );
}
